import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { InlineHint } from "../../../UIToolkit/molecules/InlineHint";
import SystemButton from "../../../UIToolkit/SystemButton/SystemButton";

type Props = {
	/** Closes the sheet — the settings link leaves it. */
	onClose: () => void;
	testIdPrefix: string;
};

/**
 * "Fast grab", reported and never promised (P3-9): auto-approval is department configuration the backend evaluates, not
 * a per-post setting, so the sheet only says how many rules the department has — never what THIS post will do. Drawn
 * only when the flag is on and a rule exists; the settings link only for support, who alone can open that page.
 */
export const GapsPostAutoApproveHint: FC<Props> = ({ onClose, testIdPrefix }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { userInfo } = useAuth();
	const { chosenDepartment } = useHospitalData();
	const isAutoApproveEnabled = useFeatureFlag(FEATURE_FLAGS.AutoApproveShifts);
	// DEFAULT: `autoApproveRules` is optional on the department; absent means none are configured.
	const autoApproveRuleCount = (chosenDepartment.autoApproveRules ?? []).length;
	if (!isAutoApproveEnabled || autoApproveRuleCount === 0) return null;

	return (
		<Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
			<InlineHint
				tone="info"
				icon={<BoltOutlinedIcon />}
				dataTestId={`${testIdPrefix}-auto-approve`}
				actionPlacement="below"
				action={
					// The auto-approve rules live on a SUPPORT-only settings page; anyone else would be sent to a redirect.
					userInfo.support ? (
						<SystemButton
							buttonVariant="borderless"
							size="compact"
							noFullWidthMobile
							onClick={() => {
								onClose();
								navigate(`/${chosenDepartment.department_id}/settings/department-configuration`);
							}}
							data-testid={`${testIdPrefix}-auto-approve-settings`}
						>
							{t("gaps.postSheet.autoApproveSettings")}
						</SystemButton>
					) : undefined
				}
			>
				{t("gaps.postSheet.autoApprove", { count: autoApproveRuleCount })}
			</InlineHint>
		</Box>
	);
};
